import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useAuth } from '@/auth/useAuth';
import { ApiError } from '@/api/client';
import { addDeviceToRequest, listAddedDevices, removeAddedDevice } from '@/api/hardwareRequests';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatDate } from '@/lib/dateFormat';
import { stockReading } from '@/pages/HardwareCatalogue/deviceStock';
import type { AddableDevice, AddedDevice, HardwareRequestDetail } from '@/types/oscar';

/** Declined means nothing is being given, so there is nothing to add a device to. */
function canAddToRequest(request: Pick<HardwareRequestDetail, 'ApprovalStatus'>) {
  return request.ApprovalStatus !== 'Declined';
}

/**
 * Add a device the requester forgot (oscar migration 123).
 *
 * <p>Anthony, 15/10/2026: <i>"on the hardware request the approver can add
 * additional devices (if the person had forgot as an example)."</i> The case that
 * prompted it was a headset: the request named what the requester ticked, and the
 * only way to add the missing item was a second request, a second ticket and a
 * second approval for what was one conversation.</p>
 *
 * ⛔ ADDED BESIDE, NOT INSTEAD. What the requester ticked is untouched, and what an
 * approver added is listed separately with who added it - so "what they asked for"
 * and "what an approver added" never blur in a record read months later.
 *
 * ⛔ APPROVERS ONLY, the same people who can decide, reading included. Helpdesk
 * sees the devices on the request and nothing to press.
 *
 * ⚠ AN ADDED DEVICE IS A DEVICE LIKE ANY OTHER FROM HERE ON. It appears in the
 * availability list, is offered stock when the request is fulfilled, and goes
 * into the decision comment - because the server reads the request's devices
 * through one view and an added device is one more row in it.
 *
 * ⚠ DEPARTMENT ENTITLEMENT IS NOT APPLIED. It narrows what a person is OFFERED on
 * the form; it is not a rule about what an approver may decide to give them, and
 * applying it would stop the approver fixing the request they are looking at. Said
 * on the screen, so nobody reads the absence as a bug.
 */
export function AddDevices({ request }: { request: HardwareRequestDetail }) {
  const { canApproveHardware } = useAuth();
  const queryClient = useQueryClient();
  const submissionId = request.SubmissionId;
  const allowed = canApproveHardware && canAddToRequest(request);

  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AddedDevice | null>(null);

  // ⚠ READ WHENEVER THE APPROVER IS LOOKING AT THE REQUEST, not only when the
  // dialog opens: what was already added is part of what the request is for, and
  // has to be on the page before anybody reaches for the button.
  const added = useQuery({
    queryKey: ['hardware-request', submissionId, 'added-devices'],
    queryFn: ({ signal }) => listAddedDevices(submissionId, signal),
    enabled: allowed,
  });

  const refresh = () => {
    // The whole request: the availability list, the fulfilment picker and the
    // decision all read the devices this changes.
    void queryClient.invalidateQueries({ queryKey: ['hardware-request', submissionId] });
  };

  const add = useMutation({
    // ⛔ ONE AT A TIME, in the order ticked. A stop at the first refusal says
    // which device was the problem, and nothing after it is attempted.
    mutationFn: async () => {
      for (const deviceOptionId of picked) {
        await addDeviceToRequest(submissionId, deviceOptionId, note.trim() || undefined);
      }
    },
    onSuccess: () => {
      setPicked(new Set());
      setNote('');
      setError(null);
      setOpen(false);
      refresh();
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : 'Could not add the device.');
      // Whatever landed before the refusal is on the request now.
      refresh();
    },
  });

  const takeOff = useMutation({
    mutationFn: (device: AddedDevice) => removeAddedDevice(submissionId, device.DeviceOptionId),
    onSuccess: () => {
      setError(null);
      setRemoving(null);
      refresh();
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Could not take the device off this request.'),
  });

  if (!allowed) return null;

  const toggle = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const data = added.data;
  const addedList = data?.Added ?? [];

  return (
    <div className="mt-3 border-t border-border pt-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-darkGrey/60">
        Added by an approver
      </h3>

      {addedList.length === 0 ? (
        <p className="mt-1 text-sm text-darkGrey/80">
          Nothing added. If the requester forgot something, add it here — it is offered stock and goes
          into the decision like any other device, and what they ticked is not changed.
        </p>
      ) : (
        <ul className="mt-1 space-y-1">
          {addedList.map((a) => (
            <li key={a.DeviceOptionId} className="flex flex-wrap items-baseline gap-x-3 text-sm text-darkGrey">
              <span className="font-semibold text-navy">{a.DeviceName}</span>
              <span className="text-xs text-darkGrey/70">
                Added by {a.AddedBy ?? 'an approver'}
                {a.AddedAtUtc && <> on {formatDate(a.AddedAtUtc)}</>}
                {a.Note && <> — {a.Note}</>}
              </span>
              <button
                type="button"
                aria-label={`Take ${a.DeviceName} off this request`}
                className="text-xs font-semibold text-navy hover:underline"
                onClick={() => {
                  setError(null);
                  setRemoving(a);
                }}
              >
                Take off
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={() => {
          setPicked(new Set());
          setNote('');
          setError(null);
          setOpen(true);
        }}
        className="mt-2 rounded border border-navy bg-white px-3 py-1.5 text-sm font-semibold text-navy hover:bg-bg"
      >
        Add a device
      </button>

      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setError(null);
        }}
        title="Add a device to this request"
        description={
          <>
            Choose what to add for {request.EmployeeName}. It is offered stock when the request is
            fulfilled and goes into the decision.
          </>
        }
        confirmLabel={add.isPending ? 'Adding…' : 'Add'}
        confirmDisabled={picked.size === 0 || add.isPending}
        closeOnConfirm={false}
        onConfirm={() => add.mutate()}
        wide
      >
        {added.isLoading && <p className="text-sm text-darkGrey/70">Loading the catalogue…</p>}

        {added.isError && (
          <p role="alert" className="text-sm text-red">
            Could not read the catalogue. If this keeps happening, contact Onsite IT Support.
          </p>
        )}

        {data && (
          <>
            {/* ⚠ SAID, NOT JUST APPLIED. The list is short for a reason, and a
                missing device otherwise reads as a bug. */}
            {data.ForLocation && (
              <p className="mb-2 flex items-start gap-2 rounded border border-border bg-bg px-3 py-2 text-sm text-darkGrey/80">
                <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>
                  This request is for a <strong>location</strong>, so only devices for a location are
                  offered.
                </span>
              </p>
            )}

            {data.Candidates.length === 0 ? (
              <p className="text-sm text-darkGrey/80">There is nothing left to add.</p>
            ) : (
              <fieldset className="max-h-72 overflow-y-auto rounded border border-border">
                <legend className="sr-only">Devices</legend>
                <ul className="divide-y divide-border">
                  {data.Candidates.map((d) => (
                    <CandidateRow
                      key={d.DeviceOptionId}
                      device={d}
                      checked={picked.has(d.DeviceOptionId)}
                      onToggle={() => toggle(d.DeviceOptionId)}
                    />
                  ))}
                </ul>
              </fieldset>
            )}

            <p className="mt-2 text-xs text-darkGrey/70">
              Department entitlement is not applied here — you are deciding this request, so any device
              on the form can be added.
            </p>

            <label className="mt-3 block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-darkGrey/70">
                Note (optional)
              </span>
              <input
                className="w-full rounded border border-border px-2 py-1.5 text-sm"
                value={note}
                maxLength={400}
                placeholder="e.g. the requester forgot the headset"
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
          </>
        )}

        {error && (
          <p role="alert" className="mt-3 rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
            {error}
          </p>
        )}
      </AlertDialog>

      <AlertDialog
        open={removing !== null}
        onOpenChange={(next) => {
          if (!next) setRemoving(null);
        }}
        title={`Take ${removing?.DeviceName ?? 'this device'} off the request?`}
        description={
          <>
            It stops being offered stock and drops out of the decision. What the requester ticked is
            not affected.
          </>
        }
        confirmLabel={takeOff.isPending ? 'Taking off…' : 'Take it off'}
        confirmDisabled={takeOff.isPending}
        closeOnConfirm={false}
        onConfirm={() => {
          if (removing) takeOff.mutate(removing);
        }}
      >
        {error && removing && (
          <p role="alert" className="rounded border border-red/40 bg-red/10 px-3 py-2 text-sm text-darkGrey">
            {error}
          </p>
        )}
      </AlertDialog>
    </div>
  );
}

function CandidateRow({
  device: d,
  checked,
  onToggle,
}: {
  device: AddableDevice;
  checked: boolean;
  onToggle: () => void;
}) {
  const already = d.AlreadyOnRequest;
  // ⚠ The same wording the catalogue uses, so "Not stocked" means one thing on
  // both screens - and means "bought per request", not a fault.
  const stock = stockReading({
    StockLines: d.StockLines, TotalOnHand: d.TotalOnHand, IsLow: false, IsAccessoryOnly: false,
  });

  return (
    <li className="px-3 py-2">
      <label className={`flex items-start gap-3 ${already ? 'opacity-60' : 'cursor-pointer'}`}>
        <input
          type="checkbox"
          className="mt-1"
          checked={checked}
          disabled={already}
          onChange={onToggle}
          aria-label={d.Name}
        />
        <span className="text-sm text-darkGrey">
          <span className="font-semibold text-navy">{d.Name}</span>
          <span className="block text-xs text-darkGrey/70" title={stock.hint}>
            {already ? 'Already on this request' : stock.label}
          </span>
        </span>
      </label>
    </li>
  );
}
